/* eslint-disable */
import React, { useEffect, useRef, useState } from 'react';
import Modal from '../../../../shared/modal';
import SelectCheckbox from '../../../../shared/select-checkbox';
import TripDatePicker from '../components/TripDatePicker';
import { useAppDispatch } from '../../../../app/hooks';
import {
    tryToCreateTrip,
    tryToEditTrip,
    tryToFetchCarriageTemplates,
    tryToFetchRolesForTrip,
    tryToFetchTrains,
} from '../../../../store/admin/trips/adminTripsSlice';
import Error from '../../../../shared/error';
import { getDateRange } from '../../../../utils/fix-uct-time';
import { tryToDeleteAssignedCabin } from '../../../../store/admin/trips/feedbacksSlice';
import { useTheme } from '../../../../utils/theme-context';
import { romeDate, fromRomeDate } from '../../../../utils/date';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */
interface CarriageTemplate {
    _id: string;
    technicalName: string;
    type: string;
    numberOfCabins: number;
    allowedCommercialNames: string[];
    defaultIcons?: string[];
}

interface CarriageRow {
    id: string;
    mode: 'template' | 'freetext';
    technicalName: string;
    commercialName: string;
    type: string;
    numberOfCabins: number | null;
    allowedCommercialNames: string[];
}

const CARRIAGE_TYPE_OPTIONS = ['Deluxe', 'Suite', 'Staff', 'Restaurant', 'Bar'];

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */
let _rowId = 0;
const nextRowId = () => `row-${++_rowId}-${Date.now()}`;

const createEmptyRow = (mode: 'template' | 'freetext' = 'freetext'): CarriageRow => ({
    id: nextRowId(),
    mode,
    technicalName: '',
    commercialName: '',
    type: '',
    numberOfCabins: null,
    allowedCommercialNames: [],
});

/* ------------------------------------------------------------------ */
/*  Step Header                                                        */
/* ------------------------------------------------------------------ */
const StepHeader = ({ number, title, subtitle }: { number: number; title: string; subtitle: string }) => (
    <div className='flex items-start gap-6 mb-6'>
        <div
            className='flex items-center justify-center bg-[#F5F5F5] dark:bg-[#252B37] text-[#0A0D12] dark:text-white'
            style={{
                width: 70,
                height: 70,
                minWidth: 70,
                padding: 20,
                borderRadius: 24,
                fontFamily: 'Inter, sans-serif',
                fontWeight: 400,
                fontSize: 36,
                lineHeight: '32px',
                textAlign: 'center',
            }}
        >
            {number}
        </div>
        <div className='pt-3 flex gap-1 flex-col'>
            <h3 className='text-[18px] font-semibold text-[#0A0D12] leading-tight font-inter font-semibold text-[20px] text-[#0A0D12] dark:text-white'>{title}</h3>
            <p className='text-[14px] text-[#717680] dark:text-white mt-0.5 font-inter font-normal text-[14px] text-[#717680]'>{subtitle}</p>
        </div>
    </div>
);

/* ------------------------------------------------------------------ */
/*  Subway Icon (reusable inline SVG)                                  */
/* ------------------------------------------------------------------ */
const SubwayIcon = ({ size = 24, color = '#252B37' }: { size?: number; color?: string }) => (
    <svg width={size} height={size} viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
        <mask id='mask_subway' style={{ maskType: 'alpha' }} maskUnits='userSpaceOnUse' x='0' y='0' width='24' height='24'>
            <rect width='24' height='24' fill='#D9D9D9' />
        </mask>
        <g mask='url(#mask_subway)'>
            <path d='M7.69225 18.5C6.79875 18.5 6.04333 18.1913 5.426 17.574C4.80867 16.9567 4.5 16.2013 4.5 15.3077V6C4.5 4.777 5.09358 3.88792 6.28075 3.33275C7.46792 2.77758 9.37433 2.5 12 2.5C14.6872 2.5 16.6089 2.76992 17.7653 3.30975C18.9218 3.84942 19.5 4.74617 19.5 6V15.3077C19.5 16.2013 19.1913 16.9567 18.574 17.574C17.9567 18.1913 17.2013 18.5 16.3077 18.5L17.6635 19.4018C17.8442 19.5224 17.9028 19.6908 17.8395 19.9068C17.776 20.1228 17.6321 20.2307 17.4078 20.2307H6.59225C6.36792 20.2307 6.224 20.1228 6.1605 19.9068C6.09717 19.6908 6.15583 19.5224 6.3365 19.4018L7.69225 18.5ZM6 10.5H11.25V6.76925H6V10.5ZM12.75 10.5H18V6.76925H12.75V10.5ZM9.42775 15.4277C9.68108 15.1746 9.80775 14.8653 9.80775 14.5C9.80775 14.1347 9.68108 13.8254 9.42775 13.5723C9.17458 13.3189 8.86533 13.1923 8.5 13.1923C8.13467 13.1923 7.82542 13.3189 7.57225 13.5723C7.31892 13.8254 7.19225 14.1347 7.19225 14.5C7.19225 14.8653 7.31892 15.1746 7.57225 15.4277C7.82542 15.6811 8.13467 15.8077 8.5 15.8077C8.86533 15.8077 9.17458 15.6811 9.42775 15.4277ZM16.4277 15.4277C16.6811 15.1746 16.8077 14.8653 16.8077 14.5C16.8077 14.1347 16.6811 13.8254 16.4277 13.5723C16.1746 13.3189 15.8653 13.1923 15.5 13.1923C15.1347 13.1923 14.8254 13.3189 14.5723 13.5723C14.3189 13.8254 14.1923 14.1347 14.1923 14.5C14.1923 14.8653 14.3189 15.1746 14.5723 15.4277C14.8254 15.6811 15.1347 15.8077 15.5 15.8077C15.8653 15.8077 16.1746 15.6811 16.4277 15.4277ZM7.69225 17H16.3077C16.7859 17 17.1875 16.8375 17.5125 16.5125C17.8375 16.1875 18 15.7859 18 15.3077V12H6V15.3077C6 15.7859 6.1625 16.1875 6.4875 16.5125C6.8125 16.8375 7.21408 17 7.69225 17ZM12 4C10.368 4 9.0795 4.10417 8.1345 4.3125C7.18967 4.52083 6.5705 4.83975 6.277 5.26925H17.7848C17.5424 4.83975 16.9488 4.52083 16.0038 4.3125C15.0589 4.10417 13.7243 4 12 4Z' fill={color} />
        </g>
    </svg>
);

/* small cabin/door icon */
const CabinIcon = ({ size = 16 }: { size?: number }) => (
    <svg width={size} height={size} viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'>
        <path d='M2 14V5.5L8 2L14 5.5V14H9.5V9.5H6.5V14H2Z' stroke='#717680' strokeWidth='1.2' strokeLinejoin='round' />
    </svg>
);

/* ------------------------------------------------------------------ */
/*  Main Component                                                     */
/* ------------------------------------------------------------------ */
const ManageTripsModal = ({
    open,
    data,
    journeys,
    onClose,
    onSaveSuccessful,
}: {
    open: boolean;
    data?: any;
    journeys?: any;
    onClose: () => void;
    onSaveSuccessful: (message: string) => void;
}) => {
    const dispatch = useAppDispatch();

	const { theme } = useTheme();
    /* refs for scroll-to-section */
    const scrollRef = useRef<HTMLDivElement>(null);
    const section1Ref = useRef<HTMLDivElement>(null);
    const section2Ref = useRef<HTMLDivElement>(null);
    const section3Ref = useRef<HTMLDivElement>(null);

    /* state */
    const [tripData, setTripData] = useState<any>({});
    const [carriageTemplates, setCarriageTemplates] = useState<CarriageTemplate[]>([]);
    const [defaultCarriageMode, setDefaultCarriageMode] = useState<'template' | 'freetext'>('freetext');
    const [carriages, setCarriages] = useState<CarriageRow[]>([createEmptyRow('freetext')]);
    const [activeSection, setActiveSection] = useState(1);
    const [loadingSubmit, setLoadingSubmit] = useState(false);
    const [submitErr, setSubmitErr] = useState<string>();
    const [selectedToDelete, setSelectedToDelete] = useState<any[]>([]);

    /* train options */
    const [trains, setTrains] = useState<any[]>([]);

    /* role-based dropdown options (fetched from /staff-users/roles-for-trip) */
    const [trainManagers, setTrainManagers] = useState<any[]>([]);
    const [headConductors, setHeadConductors] = useState<any[]>([]);
    const [headBarmen, setHeadBarmen] = useState<any[]>([]);
    const [maitres, setMaitres] = useState<any[]>([]);
    const [guestRelations, setGuestRelations] = useState<any[]>([]);

    /* second Head Barman prompt & state */
    const [showSecondBarmanPrompt, setShowSecondBarmanPrompt] = useState(false);
    const [hasSecondBarman, setHasSecondBarman] = useState(false);

    /* ---- Fetch carriage templates + role data ---- */
    useEffect(() => {
        if (!open) return;
        const fetchTemplates = async () => {
            try {
                const res = await dispatch(tryToFetchCarriageTemplates()).unwrap();
                setCarriageTemplates(Array.isArray(res) ? res : []);
            } catch (err) {
                console.error('Failed to fetch carriage templates:', err);
            }
        };
        const fetchRoles = async () => {
            try {
                const res = await dispatch(tryToFetchRolesForTrip()).unwrap();
                const fmt = (list: any[]) =>
                    (list || []).map((u: any) => ({
                        ...u,
                        label: `${u?.name || ''} ${u?.lastName || ''}`.trim() || u?.username || u?.email,
                        value: u?._id,
                    }));
                setTrainManagers(fmt(res?.trainManagers));
                setHeadConductors(fmt(res?.headConductors));
                setHeadBarmen(fmt(res?.headBarmen));
                setMaitres(fmt(res?.maitres));
                setGuestRelations(fmt(res?.guestRelations));
            } catch (err) {
                console.error('Failed to fetch roles for trip:', err);
            }
        };
        const fetchTrains = async () => {
            try {
                const res = await dispatch(tryToFetchTrains()).unwrap();
                const list = Array.isArray(res) ? res : [];
                setTrains(
                    list.map((t: any) => ({
                        ...t,
                        label: t?.name || t?.trainName || '',
                        value: t?._id,
                    })),
                );
            } catch (err) {
                console.error('Failed to fetch trains:', err);
            }
        };
        fetchTemplates();
        fetchRoles();
        fetchTrains();
    }, [open, dispatch]);

    /* ---- Populate from data (edit mode) ---- */
    useEffect(() => {
        if (data) {
            /* Extract role-based crew member IDs from assignedCabins */
            const cabins: any[] = data?.assignedCabins || [];
            const findByRole = (role: string) =>
                cabins.find((c: any) => c.role === role)?.crewMemberId || '';

            setTripData({
                trainManagerId: data?.trainManagerId,
                trainId: data?.trainId,
                itineraryId: data?.itineraryId,
                startDate: data?.startDate,
                endDate: data?.endDate,
                headConductorId: data?.headConductorId || findByRole('head_conductor'),
                headBarmanId: data?.headBarmanId || findByRole('head_barman'),
                headBarman2Id: data?.headBarman2Id || '',
                maitreId: data?.maitreId || findByRole('maitre'),
                guestRelationsId: data?.guestRelationsId || findByRole('guest_relations_manager') || findByRole('guest_relations'),
            });
            if (data?.headBarman2Id) {
                setHasSecondBarman(true);
            }
            if (data?.trainComposition?.length > 0) {
                setCarriages(
                    data.trainComposition.map((c: any) => {
                        /* Auto-detect mode: if technicalName matches a template → template mode */
                        const matchesTemplate = carriageTemplates.some(
                            (t) => t.technicalName === c.technicalName,
                        );
                        const tpl = matchesTemplate
                            ? carriageTemplates.find((t) => t.technicalName === c.technicalName)
                            : null;
                        return {
                            id: nextRowId(),
                            mode: matchesTemplate ? 'template' as const : 'freetext' as const,
                            technicalName: c.technicalName || '',
                            commercialName: c.commercialName || '',
                            type: c.type || '',
                            numberOfCabins: c.numberOfCabins ?? null,
                            allowedCommercialNames: tpl?.allowedCommercialNames || [],
                        };
                    }),
                );
            }
        }
    }, [open, data, carriageTemplates]);

    /* ---- Scroll spy for active section indicator ---- */
    useEffect(() => {
        const container = scrollRef.current;
        if (!container) return;
        const handleScroll = () => {
            const refs = [section1Ref, section2Ref, section3Ref];
            for (let i = refs.length - 1; i >= 0; i--) {
                const el = refs[i].current;
                if (el && el.offsetTop - container.scrollTop <= 80) {
                    setActiveSection(i + 1);
                    break;
                }
            }
        };
        container.addEventListener('scroll', handleScroll);
        return () => container.removeEventListener('scroll', handleScroll);
    }, []);

    /* ---- Close / reset ---- */
    const onCloseModal = () => {
        if (loadingSubmit) return;
        setTripData({});
        setDefaultCarriageMode('freetext');
        setCarriages([createEmptyRow('freetext')]);
        setSubmitErr(undefined);
        setSelectedToDelete([]);
        setActiveSection(1);
        setTrains([]);
        setTrainManagers([]);
        setHeadConductors([]);
        setHeadBarmen([]);
        setShowSecondBarmanPrompt(false);
        setHasSecondBarman(false);
        setMaitres([]);
        setGuestRelations([]);
        onClose();
    };

    /* ---- Scroll to section ---- */
    const scrollToSection = (step: number) => {
        const refs = [section1Ref, section2Ref, section3Ref];
        refs[step - 1]?.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    /* ================================================================ */
    /*  Step 1 handlers                                                 */
    /* ================================================================ */
    const onChangeSelect = (value: any, type?: string) => {
        if (type) {
            setTripData((prev: any) => ({ ...prev, [type]: value?.value }));
            if (type === 'headBarmanId' && value?.value && !hasSecondBarman) {
                setShowSecondBarmanPrompt(true);
            }
        }
    };

    const onChangeStartDate = (date: Date | null) => {
        setTripData((prev: any) => ({ ...prev, startDate: date ? fromRomeDate(date) : null }));
    };

    const onChangeEndDate = (date: Date | null) => {
        setTripData((prev: any) => ({ ...prev, endDate: date ? fromRomeDate(date) : null }));
    };

    /* ================================================================ */
    /*  Step 2 handlers                                                 */
    /* ================================================================ */
    /* ── Mode toggle ── */
    const onToggleMode = (idx: number, mode: 'template' | 'freetext') => {
        setCarriages((prev) =>
            prev.map((row, i) => {
                if (i !== idx) return row;
                /* Reset row when switching modes */
                return {
                    ...row,
                    mode,
                    technicalName: '',
                    commercialName: '',
                    type: '',
                    numberOfCabins: null,
                    allowedCommercialNames: [],
                };
            }),
        );
    };

    /* ── Template mode handlers ── */
    const onSelectTechnicalName = (idx: number, value: string) => {
        const template = carriageTemplates.find((t) => t.technicalName === value);
        setCarriages((prev) =>
            prev.map((row, i) => {
                if (i !== idx) return row;
                if (template) {
                    const allowed = template.allowedCommercialNames || [];
                    /* Filter out names already used by other rows */
                    const available = allowed.filter(
                        (letter) => !usedCommercialNames.includes(letter) || letter === row.commercialName,
                    );
                    /* Auto-select if only one option is available (e.g. RESTAURANT, BAR) */
                    const autoCommercial = available.length === 1 ? available[0] : '';
                    return {
                        ...row,
                        technicalName: template.technicalName,
                        type: template.type,
                        numberOfCabins: template.numberOfCabins,
                        allowedCommercialNames: allowed,
                        commercialName: autoCommercial,
                    };
                }
                return { ...row, technicalName: value, type: '', numberOfCabins: null, allowedCommercialNames: [], commercialName: '' };
            }),
        );
    };

    const onSelectCommercialName = (idx: number, value: string) => {
        setCarriages((prev) => prev.map((row, i) => (i === idx ? { ...row, commercialName: value } : row)));
    };

    /* ── Free-text mode handlers ── */
    const FREETEXT_PRESETS: { prefix: string; type: string; numberOfCabins: number; commercialName?: string }[] = [
        { prefix: 'VLL', type: 'Deluxe', numberOfCabins: 6 },
        { prefix: 'VLS', type: 'Suite', numberOfCabins: 4 },
        { prefix: 'VST', type: 'Staff', numberOfCabins: 1, commercialName: 'STAFF' },
        { prefix: 'VB', type: 'Bar', numberOfCabins: 1, commercialName: 'BAR' },
        { prefix: 'VR', type: 'Restaurant', numberOfCabins: 1, commercialName: 'RESTAURANT' },
    ];

    const onFreeTextChange = (idx: number, field: keyof CarriageRow, value: string | number | null) => {
        setCarriages((prev) =>
            prev.map((row, i) => {
                if (i !== idx) return row;
                const updated = { ...row, [field]: value };
                /* Auto-fill type & numberOfCabins based on technicalName prefix */
                if (field === 'technicalName' && typeof value === 'string') {
                    const upper = value.toUpperCase();
                    const preset = FREETEXT_PRESETS.find((p) => upper.startsWith(p.prefix));
                    if (preset) {
                        updated.type = preset.type;
                        updated.numberOfCabins = preset.numberOfCabins;
                        if (preset.commercialName) {
                            updated.commercialName = preset.commercialName;
                        }
                    } else {
                        /* Clear auto-filled values if prefix no longer matches */
                        const prevPreset = FREETEXT_PRESETS.find((p) => (row.technicalName || '').toUpperCase().startsWith(p.prefix));
                        if (prevPreset) {
                            updated.type = '';
                            updated.numberOfCabins = null;
                            if (prevPreset.commercialName) {
                                updated.commercialName = '';
                            }
                        }
                    }
                }
                return updated;
            }),
        );
    };

    const addCarriageRow = () => setCarriages((prev) => [...prev, createEmptyRow(defaultCarriageMode)]);

    /** Switch the default mode and also flip any rows that are still empty */
    const isRowEmpty = (row: CarriageRow) => !row.technicalName && !row.commercialName && !row.type && !row.numberOfCabins;
    const onSwitchDefaultMode = (mode: 'template' | 'freetext') => {
        setDefaultCarriageMode(mode);
        setCarriages((prev) =>
            prev.map((row) => (isRowEmpty(row) ? { ...row, mode } : row)),
        );
    };

    const removeCarriageRow = (idx: number) => {
        if (carriages.length <= 1) return;
        setCarriages((prev) => prev.filter((_, i) => i !== idx));
    };

    /* collect used commercial names to prevent duplicates */
    const usedCommercialNames = carriages.map((c) => c.commercialName).filter(Boolean);

    /* collect used technical names to prevent duplicates */
    const usedTechnicalNames = carriages.map((c) => c.technicalName).filter(Boolean);

    /* ================================================================ */
    /*  Step 3 – slot-based drag-and-drop                               */
    /*  Commercial name labels are FIXED. Only the cards swap.          */
    /* ================================================================ */
    /* Fixed commercial-name slots A–G, always rendered.                 */
    /* Each slot looks up whether a carriage row is assigned to it.      */
    /* Build layout slots: standard A–G + any extra commercial names from free-text carriages */
    const BASE_COMMERCIAL_NAMES = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L'];
    const extraLetters = carriages
        .filter((c) => c.commercialName && c.technicalName && !BASE_COMMERCIAL_NAMES.includes(c.commercialName))
        .map((c) => c.commercialName);
    const ALL_COMMERCIAL_NAMES = [...BASE_COMMERCIAL_NAMES, ...extraLetters.filter((l, i) => extraLetters.indexOf(l) === i)];
    const slotsForLayout = ALL_COMMERCIAL_NAMES.map((letter) => {
        const match = carriages.find((c) => c.commercialName === letter && c.technicalName);
        return {
            commercialName: letter,
            technicalName: match?.technicalName || '',
            type: match?.type || '',
            numberOfCabins: match?.numberOfCabins ?? null,
            id: match?.id || '',
        };
    });

    const [dragSlotIdx, setDragSlotIdx] = useState<number | null>(null);
    const [dropTargetIdx, setDropTargetIdx] = useState<number | null>(null);

    const onSlotDragStart = (e: React.DragEvent, slotIdx: number) => {
        const slot = slotsForLayout[slotIdx];
        if (!slot?.technicalName) {
            e.preventDefault();
            return;
        }
        setDragSlotIdx(slotIdx);
        e.dataTransfer.effectAllowed = 'move';
    };

    const onSlotDragOver = (e: React.DragEvent, slotIdx: number) => {
        e.preventDefault();
        if (dragSlotIdx === null || dragSlotIdx === slotIdx) {
            setDropTargetIdx(null);
            return;
        }

        const src = slotsForLayout[dragSlotIdx];
        const dst = slotsForLayout[slotIdx];
        if (!src?.technicalName) return;

        /* For template carriages, check allowed commercial names; free-text can go anywhere */
        const srcRow = carriages.find((r) => r.id === src.id);
        const srcIsTemplate = srcRow?.mode === 'template';
        if (srcIsTemplate) {
            const srcTemplate = carriageTemplates.find((t) => t.technicalName === src.technicalName);
            const srcCanGoToDst = srcTemplate?.allowedCommercialNames.includes(dst.commercialName);
            if (!srcCanGoToDst) {
                setDropTargetIdx(null);
                e.dataTransfer.dropEffect = 'none';
                return;
            }
        }

        if (dst.technicalName) {
            const dstRow = carriages.find((r) => r.id === dst.id);
            const dstIsTemplate = dstRow?.mode === 'template';
            if (dstIsTemplate) {
                const dstTemplate = carriageTemplates.find((t) => t.technicalName === dst.technicalName);
                const dstCanGoToSrc = dstTemplate?.allowedCommercialNames.includes(src.commercialName);
                if (!dstCanGoToSrc) {
                    setDropTargetIdx(null);
                    e.dataTransfer.dropEffect = 'none';
                    return;
                }
            }
        }

        setDropTargetIdx(slotIdx);
        e.dataTransfer.dropEffect = 'move';
    };

    const onSlotDrop = (e: React.DragEvent, slotIdx: number) => {
        e.preventDefault();
        if (dragSlotIdx === null || dragSlotIdx === slotIdx) return;

        const src = slotsForLayout[dragSlotIdx];
        const dst = slotsForLayout[slotIdx];
        if (!src?.technicalName) return;

        /* Validate: template carriages must have allowedCommercialNames; free-text can swap freely */
        const srcRow = carriages.find((r) => r.id === src.id);
        if (!srcRow) return;
        if (srcRow.mode === 'template') {
            const srcTemplate = carriageTemplates.find((t) => t.technicalName === src.technicalName);
            if (!srcTemplate?.allowedCommercialNames.includes(dst.commercialName)) return;
        }

        if (dst.technicalName) {
            /* both slots have cards – swap their commercial names */
            const dstRow = carriages.find((r) => r.id === dst.id);
            if (!dstRow) return;
            if (dstRow.mode === 'template') {
                const dstTemplate = carriageTemplates.find((t) => t.technicalName === dst.technicalName);
                if (!dstTemplate?.allowedCommercialNames.includes(src.commercialName)) return;
            }

            setCarriages((prev) =>
                prev.map((row) => {
                    if (row.id === srcRow.id) return { ...row, commercialName: dst.commercialName };
                    if (row.id === dstRow.id) return { ...row, commercialName: src.commercialName };
                    return row;
                }),
            );
        } else {
            /* destination slot is empty – move the source card to that commercial name */
            setCarriages((prev) =>
                prev.map((row) => {
                    if (row.id === srcRow.id) return { ...row, commercialName: dst.commercialName };
                    return row;
                }),
            );
        }

        setDragSlotIdx(null);
        setDropTargetIdx(null);
    };

    const onSlotDragEnd = () => {
        setDragSlotIdx(null);
        setDropTargetIdx(null);
    };

    /* ================================================================ */
    /*  Submit                                                          */
    /* ================================================================ */
    const handleSubmit = async () => {
        try {
            if (!tripData?.itineraryId) {
                setSubmitErr('Please select an itinerary');
                scrollToSection(1);
                return;
            }
            if (!tripData?.startDate || !tripData?.endDate) {
                setSubmitErr('Please select a date range');
                scrollToSection(1);
                return;
            }

            const validCarriages = carriages.filter((c) => c.technicalName && c.commercialName);

            /* Required fields check for free-text carriages */
            for (const c of validCarriages) {
                if (c.mode === 'freetext') {
                    if (!c.type) {
                        setSubmitErr(`Free-text carriage "${c.technicalName}" is missing a type`);
                        scrollToSection(2);
                        return;
                    }
                    if (!c.numberOfCabins || c.numberOfCabins < 1) {
                        setSubmitErr(`Free-text carriage "${c.technicalName}" needs a valid number of cabins (>= 1)`);
                        scrollToSection(2);
                        return;
                    }
                }
            }

            const trainComposition = validCarriages.map((c) => ({
                technicalName: c.technicalName,
                commercialName: c.commercialName,
                carriageCode: `${c.technicalName} ${c.commercialName}`,
                type: c.type,
                numberOfCabins: c.numberOfCabins,
            }));

            /* duplicate commercial-name check (skip special names like STAFF, BAR, RESTAURANT) */
            const SPECIAL_NAMES = ['STAFF', 'RESTAURANT', 'BAR'];
            const letterNames = trainComposition.map((c) => c.commercialName).filter((n) => !SPECIAL_NAMES.includes(n.toUpperCase()));
            if (new Set(letterNames).size !== letterNames.length) {
                setSubmitErr('Each commercial name (letter) must be unique per trip');
                scrollToSection(2);
                return;
            }


            setLoadingSubmit(true);
            setSubmitErr(undefined);

            /* handle deletions for edit mode */
            if (data && selectedToDelete.length) {
                for (const a of selectedToDelete) {
                    await dispatch(tryToDeleteAssignedCabin({ tripId: data?._id, assignedCabinId: a?._id }));
                }
            }

            const range = getDateRange([tripData?.startDate, tripData?.endDate]);
            const formatData = {
                ...tripData,
                startDate: range?.dateRangeMin,
                endDate: range?.dateRangeMax,
                trainComposition,
                assignedCabins: data?.assignedCabins || [],
            };

            if (data) {
                await dispatch(tryToEditTrip({ id: data?._id, data: formatData })).unwrap();
            } else {
                await dispatch(tryToCreateTrip(formatData)).unwrap();
            }

            onSaveSuccessful('Trip saved successfully');
            setLoadingSubmit(false);
            onCloseModal();
        } catch (err) {
            setSubmitErr(`${err}`);
            setLoadingSubmit(false);
        }
    };

    /* ================================================================ */
    /*  Template options for dropdowns                                  */
    /* ================================================================ */
    const templateOptions = carriageTemplates.map((t) => ({
        label: t.technicalName,
        value: t.technicalName,
    }));

    /* ================================================================ */
    /*  Render                                                          */
    /* ================================================================ */
    return (
        <Modal
            open={open}
            title={data ? 'Edit Trip' : 'Create New Trip'}
            onClose={onCloseModal}
            titleStyle='!text-[20px] !font-semibold !text-[#0A0D12]'
            contentContainerStyle='!rounded-2xl !top-[20px] !left-[20px] !right-[20px] !bottom-[20px] !translate-x-0 !translate-y-0 !max-w-none !max-h-none !w-auto !h-auto !min-w-0 !overflow-hidden !flex !flex-col'
        >
            <div className='flex flex-col flex-1 min-h-0 overflow-hidden'>
                {/* ---------- Scrollable content ---------- */}
                <div ref={scrollRef} className='flex-1 overflow-y-auto px-2 pb-6'>
                    {/* ============================== STEP 1 ============================== */}
                    <div ref={section1Ref} className='pt-4'>
                        <StepHeader number={1} title='Main Details' subtitle='Please fill in the main details of the trip' />

                        {/* Row 1: 5 cols */}
                        <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-x-4 gap-y-1'>
                            <SelectCheckbox
                                name='Itinerary'
                                placeholder='Please select one'
                                selectedOption={tripData?.itineraryId ? journeys?.find((j: any) => j?.value === tripData.itineraryId) : undefined}
                                options={journeys || []}
                                uniqueName='itineraryId'
                                onChangeSelectedOption={onChangeSelect}
                                containerStyle='mb-1 w-full'
                            />
                            <TripDatePicker
                                label='Trip Start Date'
                                labelStyle='!font-normal !text-[14px] !text-[#0A0D12] !font-inter'
                                showTime
                                value={tripData?.startDate ? romeDate(tripData.startDate) : null}
                                onChange={onChangeStartDate}
                                maxDate={tripData?.endDate ? romeDate(tripData.endDate) : null}
                                placeholderText='Select start date'
                            />
                            <TripDatePicker
                                label='Trip End Date'
                                labelStyle='!font-normal !text-[14px] !text-[#0A0D12] !font-inter'
                                showTime
                                value={tripData?.endDate ? romeDate(tripData.endDate) : null}
                                onChange={onChangeEndDate}
                                minDate={tripData?.startDate ? romeDate(tripData.startDate) : null}
                                placeholderText='Select end date'
                            />
                            <SelectCheckbox
                                name='Select Train Manager'
                                placeholder='Please select one'
                                selectedOption={tripData?.trainManagerId ? trainManagers?.find((m: any) => m?.value === tripData.trainManagerId) : undefined}
                                options={trainManagers || []}
                                uniqueName='trainManagerId'
                                onChangeSelectedOption={onChangeSelect}
                                containerStyle='mb-1 w-full'
                            />
                            <SelectCheckbox
                                name='Select Head Conductor'
                                placeholder='Please select one'
                                selectedOption={tripData?.headConductorId ? headConductors?.find((h: any) => h?.value === tripData.headConductorId) : undefined}
                                options={headConductors || []}
                                uniqueName='headConductorId'
                                onChangeSelectedOption={onChangeSelect}
                                containerStyle='mb-1 w-full'
                            />
                        </div>

                        {/* Row 2: 5 cols */}
                        <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-x-4 gap-y-1 mt-2'>
                            <SelectCheckbox
                                name='Train'
                                placeholder='Please select a train'
                                selectedOption={tripData?.trainId ? trains?.find((t: any) => t?.value === tripData.trainId) : undefined}
                                options={trains || []}
                                uniqueName='trainId'
                                onChangeSelectedOption={onChangeSelect}
                                containerStyle='mb-1 w-full'
                            />
                            <div className='flex flex-col mb-1 w-full'>
                                <SelectCheckbox
                                    name='Select Head Barman'
                                    placeholder='Please select one'
                                    selectedOption={tripData?.headBarmanId ? headBarmen?.find((s: any) => s?.value === tripData.headBarmanId) : undefined}
                                    options={(headBarmen || []).filter((s: any) => s?.value !== tripData?.headBarman2Id)}
                                    uniqueName='headBarmanId'
                                    onChangeSelectedOption={onChangeSelect}
                                    containerStyle='w-full'
                                />
                                {showSecondBarmanPrompt && !hasSecondBarman && (
                                    <div className='mt-2 bg-[#F5F5F5] dark:bg-[#252B37] rounded-lg px-4 py-3 flex flex-col gap-2'>
                                        <p className='text-[13px] text-[#0A0D12] dark:text-white font-inter'>
                                            Do you have more than one Head Barman on this trip?
                                        </p>
                                        <div className='flex gap-2'>
                                            <button
                                                type='button'
                                                onClick={() => {
                                                    setHasSecondBarman(true);
                                                    setShowSecondBarmanPrompt(false);
                                                }}
                                                className='px-4 py-1.5 text-[12px] font-inter font-medium rounded-md bg-[#2D8EFF] text-white hover:opacity-90 transition-opacity'
                                            >
                                                Yes
                                            </button>
                                            <button
                                                type='button'
                                                onClick={() => setShowSecondBarmanPrompt(false)}
                                                className='px-4 py-1.5 text-[12px] font-inter font-medium rounded-md bg-white dark:bg-[#1E2433] text-[#0A0D12] dark:text-white border border-[#E4E7EC] dark:border-[#3B3F4A] hover:opacity-90 transition-opacity'
                                            >
                                                No
                                            </button>
                                        </div>
                                    </div>
                                )}
                                {hasSecondBarman && (
                                    <div className='mt-2'>
                                        <SelectCheckbox
                                            name='Select 2nd Head Barman'
                                            placeholder='Please select one'
                                            selectedOption={tripData?.headBarman2Id ? headBarmen?.find((s: any) => s?.value === tripData.headBarman2Id) : undefined}
                                            options={(headBarmen || []).filter((s: any) => s?.value !== tripData?.headBarmanId)}
                                            uniqueName='headBarman2Id'
                                            onChangeSelectedOption={onChangeSelect}
                                            containerStyle='w-full'
                                        />
                                    </div>
                                )}
                            </div>
                            <SelectCheckbox
                                name='Select Maitre'
                                placeholder='Please select one'
                                selectedOption={tripData?.maitreId ? maitres?.find((s: any) => s?.value === tripData.maitreId) : undefined}
                                options={maitres || []}
                                uniqueName='maitreId'
                                onChangeSelectedOption={onChangeSelect}
                                containerStyle='mb-1 w-full'
                            />
                            <SelectCheckbox
                                name='Select Guest Relations'
                                placeholder='Please select one'
                                selectedOption={tripData?.guestRelationsId ? guestRelations?.find((s: any) => s?.value === tripData.guestRelationsId) : undefined}
                                options={guestRelations || []}
                                uniqueName='guestRelationsId'
                                onChangeSelectedOption={onChangeSelect}
                                containerStyle='mb-1 w-full'
                            />
                        </div>
                    </div>

                    {/* ============================== STEP 2 ============================== */}
                    <div ref={section2Ref} className='pt-8'>
                        <StepHeader number={2} title='List the Carriages' subtitle='Write or select from previously added carriages' />

                        {/* Default mode toggle */}
                        <div className='flex items-center gap-3 mb-3'>
                            <span className='text-[13px] font-inter text-[#717680] dark:text-white'>New rows default to:</span>
                            <div className='flex items-center bg-[#F5F5F5] dark:bg-[#1E2433] rounded-lg p-0.5'>
                                <button
                                    type='button'
                                    onClick={() => onSwitchDefaultMode('freetext')}
                                    className={`text-[12px] px-3 py-1.5 rounded-md font-inter transition-colors ${defaultCarriageMode === 'freetext' ? 'bg-white dark:bg-[#2D8EFF] text-[#0A0D12] dark:text-white shadow-sm' : 'text-[#717680] dark:text-[#A4A7AE]'}`}
                                >
                                    Free text
                                </button>
                                <button
                                    type='button'
                                    onClick={() => onSwitchDefaultMode('template')}
                                    className={`text-[12px] px-3 py-1.5 rounded-md font-inter transition-colors ${defaultCarriageMode === 'template' ? 'bg-white dark:bg-[#2D8EFF] text-[#0A0D12] dark:text-white shadow-sm' : 'text-[#717680] dark:text-[#A4A7AE]'}`}
                                >
                                    Template
                                </button>
                            </div>
                        </div>

                        <div className='rounded-xl border border-[#E4E7EC] overflow-hidden'>
                            <table className='w-full border-collapse'>
                                <thead>
                                    <tr className='bg-[#FAFAFA] dark:bg-[#252B37]'>
                                        <th className='text-center py-3 px-4 text-[12px] font-medium text-[#717680] dark:text-white border-b border-r border-[#E4E7EC] w-[25%] text-[#717680] font-normal font-inter text-[13px]'>Carriage Technical Name</th>
                                        <th className='text-center py-3 px-4 text-[12px] font-medium text-[#717680] dark:text-white border-b border-r border-[#E4E7EC] w-[25%] text-[#717680] font-normal font-inter text-[13px]'>Carriage Commercial Name</th>
                                        <th className='text-center py-3 px-4 text-[12px] font-medium text-[#717680] dark:text-white border-b border-r border-[#E4E7EC] w-[25%] text-[#717680] font-normal font-inter text-[13px]'>Carriage Type</th>
                                        <th className='text-center py-3 px-4 text-[12px] font-medium text-[#717680] dark:text-white border-b border-r border-[#E4E7EC] w-[15%] text-[#717680] font-normal font-inter text-[13px]'>Number of Cabins</th>
                                        <th className='border-b border-[#E4E7EC] w-[10%]'></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {carriages.map((row, idx) => {
                                        const availableLetters = row.allowedCommercialNames.filter(
                                            (letter) => !usedCommercialNames.includes(letter) || letter === row.commercialName,
                                        );
                                        const isLast = idx === carriages.length - 1;
                                        const chevronStyle = {
                                            backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 10 10'%3E%3Cpath d='M2.5 4l2.5 2.5L7.5 4' stroke='%23717680' stroke-width='1.2' fill='none' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E\")",
                                            backgroundRepeat: 'no-repeat',
                                            backgroundPosition: 'right 8px center',
                                            paddingRight: '28px',
                                        };
                                        const isTemplate = row.mode === 'template';
                                        /* Check if the current row matches a preset (auto-filled commercial name) */
                                        const matchedPreset = !isTemplate && row.technicalName
                                            ? FREETEXT_PRESETS.find((p) => row.technicalName.toUpperCase().startsWith(p.prefix))
                                            : null;
                                        const isPresetCommercial = !!matchedPreset?.commercialName;
                                        /* Check if the free-text technicalName collides with a template */
                                        const templateCollision = !isTemplate && row.technicalName
                                            ? carriageTemplates.some((t) => t.technicalName.toLowerCase() === row.technicalName.toLowerCase())
                                            : false;
                                        /* Validate commercial name: only check duplicates for single-letter names */
                                        const commercialNameError = !isTemplate && row.commercialName && /^[A-Z]$/.test(row.commercialName)
                                            ? (usedCommercialNames.filter((n) => n === row.commercialName).length > 1 ? 'Already used' : '')
                                            : '';
                                        return (
                                            <tr key={row.id} className={`${!isLast ? 'border-b border-[#F2F4F7]' : ''} hover:bg-[#FAFAFA]/60 dark:hover:bg-[#232323]/60 transition-colors`}>
                                                {/* Technical Name */}
                                                <td className='py-3 px-4 border-r border-[#E4E7EC]'>
                                                    {isTemplate ? (
                                                        <select
                                                            value={row.technicalName}
                                                            onChange={(e) => onSelectTechnicalName(idx, e.target.value)}
                                                            className='w-full bg-transparent border-none outline-none text-[13px] text-[#0A0D12] dark:text-white cursor-pointer appearance-none'
                                                            style={chevronStyle}
                                                        >
                                                            <option value=''>Select template</option>
                                                            {templateOptions
                                                                .filter((opt) => !usedTechnicalNames.includes(opt.value) || opt.value === row.technicalName)
                                                                .map((opt) => (
                                                                    <option key={opt.value} value={opt.value}>
                                                                        {opt.label}
                                                                    </option>
                                                                ))}
                                                        </select>
                                                    ) : (
                                                        <div className='flex flex-col'>
                                                            <input
                                                                type='text'
                                                                value={row.technicalName}
                                                                onChange={(e) => onFreeTextChange(idx, 'technicalName', e.target.value)}
                                                                placeholder='e.g. MY-CUSTOM-123'
                                                                className='w-full bg-transparent border-none outline-none text-[13px] text-[#0A0D12] dark:text-white placeholder:text-[#A4A7AE]'
                                                            />
                                                        </div>
                                                    )}
                                                </td>
                                                {/* Commercial Name */}
                                                <td className='py-3 px-4 border-r border-[#E4E7EC]'>
                                                    {isTemplate ? (
                                                        <select
                                                            value={row.commercialName}
                                                            onChange={(e) => onSelectCommercialName(idx, e.target.value)}
                                                            disabled={!row.technicalName}
                                                            className='w-full bg-transparent border-none outline-none text-[13px] text-[#0A0D12] dark:text-white cursor-pointer disabled:text-[#A4A7AE] disabled:cursor-not-allowed appearance-none'
                                                            style={chevronStyle}
                                                        >
                                                            <option value=''>Select letter</option>
                                                            {availableLetters.map((letter) => (
                                                                <option key={letter} value={letter}>
                                                                    Carriage {letter}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    ) : (
                                                        <div className='flex flex-col'>
                                                            <input
                                                                type='text'
                                                                value={row.commercialName}
                                                                onChange={(e) => {
                                                                    const val = e.target.value.toUpperCase();
                                                                    onFreeTextChange(idx, 'commercialName', val);
                                                                }}
                                                                placeholder='e.g. H'
                                                                className='w-full bg-transparent border-none outline-none text-[13px] text-[#0A0D12] dark:text-white placeholder:text-[#A4A7AE]'
                                                            />
                                                            {commercialNameError && (
                                                                <span className='text-[10px] text-red-500 mt-0.5'>{commercialNameError}</span>
                                                            )}
                                                        </div>
                                                    )}
                                                </td>
                                                {/* Type */}
                                                <td className='py-3 px-4 border-r border-[#E4E7EC]'>
                                                    {isTemplate ? (
                                                        <span
                                                            className='text-[13px] inline-flex items-center w-full'
                                                            style={{
                                                                color: row.type ? (theme === 'dark' ? '#ffffff' : '#0A0D12') : '#A4A7AE',
                                                                ...(row.type ? chevronStyle : {}),
                                                            }}
                                                        >
                                                            {row.type || 'Auto-filled from template'}
                                                        </span>
                                                    ) : (
                                                        <select
                                                            value={row.type}
                                                            onChange={(e) => onFreeTextChange(idx, 'type', e.target.value)}
                                                            className='w-full bg-transparent border-none outline-none text-[13px] text-[#0A0D12] dark:text-white cursor-pointer appearance-none'
                                                            style={chevronStyle}
                                                        >
                                                            <option value=''>Select type</option>
                                                            {CARRIAGE_TYPE_OPTIONS.map((t) => (
                                                                <option key={t} value={t}>{t}</option>
                                                            ))}
                                                        </select>
                                                    )}
                                                </td>
                                                {/* Number of Cabins */}
                                                <td className='py-3 px-4 text-center border-r border-[#E4E7EC]'>
                                                    {isTemplate ? (
                                                        <span className='text-[13px]' style={{ color: row.numberOfCabins != null ? (theme === 'dark' ? '#ffffff' : '#0A0D12') : '#A4A7AE' }}>
                                                            {row.numberOfCabins != null ? row.numberOfCabins : 'Auto-filled'}
                                                        </span>
                                                    ) : (
                                                        <input
                                                            type='number'
                                                            min={1}
                                                            value={row.numberOfCabins ?? ''}
                                                            onChange={(e) => {
                                                                const v = e.target.value ? parseInt(e.target.value, 10) : null;
                                                                onFreeTextChange(idx, 'numberOfCabins', v && v > 0 ? v : null);
                                                            }}
                                                            placeholder='e.g. 4'
                                                            className='w-full bg-transparent border-none outline-none text-[13px] text-center text-[#0A0D12] dark:text-white placeholder:text-[#A4A7AE] [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none'
                                                        />
                                                    )}
                                                </td>
                                                {/* Remove */}
                                                <td className='py-3 px-2 text-center'>
                                                    {carriages.length > 1 && (
                                                        <button
                                                            onClick={() => removeCarriageRow(idx)}
                                                            className='text-[#A4A7AE] hover:text-red-500 transition-colors p-1 rounded-md hover:bg-red-50'
                                                            title='Remove row'
                                                        >
                                                            <svg width='14' height='14' viewBox='0 0 14 14' fill='none'>
                                                                <path d='M3.5 3.5l7 7M10.5 3.5l-7 7' stroke='currentColor' strokeWidth='1.4' strokeLinecap='round' />
                                                            </svg>
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>

                            {/* Add New Carriage button – inside the rounded container */}
                            <button
                                onClick={addCarriageRow}
                                className='w-full py-3 flex items-center justify-center gap-4 text-[13px] font-semibold text-black dark:text-white transition-opacity hover:opacity-90 border-t border-[#E4E7EC] text-[#0A0D12] text-[14px] !font-normal font-inter'
                                style={{ backgroundColor: theme === 'dark' ? '#252B37' : '#2D8EFF1A' }}
                            >
                                Add New Carriage
                                <svg width='14' height='14' viewBox='0 0 14 14' fill='none'>
                                    <path d='M7 2.5v9M2.5 7h9' stroke='#2D8EFF' strokeWidth='2' strokeLinecap='round' />
                                </svg>
                            </button>
                        </div>
                    </div>

                    {/* ============================== STEP 3 ============================== */}
                    <div ref={section3Ref} className='pt-8'>
                        <StepHeader
                            number={3}
                            title='Train Layout'
                            subtitle='Drag a carriage card onto another slot to swap their positions. Commercial name labels are fixed.'
                        />

                        <div className='overflow-x-auto pb-4'>
                            <div className='flex items-start gap-0 min-w-max'>
                                {/* Start block */}
                                <div className='flex flex-col items-center mr-2'>
                                    <span className='  font-inter text-[#535862] dark:text-white font-medium text-[14px]'>Start</span>
                                    <div className='w-full h-[1px] bg-[#E9EAEB] mb-2 mt-3'></div>
                                    <div className='w-[100px] h-[100px] rounded-lg pt-3  bg-white dark:bg-[#252B37] flex items-center justify-center'>
                                        <SubwayIcon size={100} color={theme === 'dark' ? '#fff' : '#252B37'} />
                                    </div>
                                </div>

                                {/* Fixed commercial-name slots with draggable cards */}
                                {slotsForLayout.map((carriage, slotIdx) => {
                                    const hasTechnical = !!carriage.technicalName;
                                    const isBeingDragged = dragSlotIdx === slotIdx;
                                    const isDropTarget = dropTargetIdx === slotIdx;
                                    return (
                                        <React.Fragment key={`slot-${carriage.commercialName}`}>
                                            {/* Arrow */}
                                            {/* <div className='flex items-center self-center mt-5 mx-1'>
                                                <span className='text-[#717680] text-lg select-none'>↔</span>
                                            </div> */}
                                             <div className='flex flex-row items-center mt-[5px] mx-1'>
                                                <img src='/svg/keyboard_arrow_down.svg' className='w-2 h-2 object-contain rotate-90' alt='download'  />
                                                           <div className='flex flex-row gap-[2px] items-center ml-[-5px] pb-[0.5px]'>
                                                                <div className='h-[1px] w-[1px] bg-[#535862] dark:bg-white	'></div>
                                                                <div className='h-[1px] w-[1px] bg-[#535862] dark:bg-white	'></div>
                                                                <div className='h-[1px] w-[1px] bg-[#535862] dark:bg-white'></div>
                                                                <div className='h-[1px] w-[1px] bg-[#535862] dark:bg-white'></div>
                                                                <div className='h-[1px] w-[1px] bg-[#535862] dark:bg-white	'></div>
                                                                	<div className='h-[1px] w-[1px] bg-[#535862] dark:bg-white	'></div>
                                                                <div className='h-[1px] w-[1px] bg-[#535862] dark:bg-white	'></div>
                                                           </div>
                                                           <div className='!h-[7px] !w-[7px] bg-[#535862] dark:bg-white rounded-[20px] ml-[1px]  '/>
                                                </div>
                                            {/* Fixed slot: label stays, card is draggable only if it has a technical name */}
                                            <div className='flex flex-col items-center'>
                                                {/* Fixed commercial name label */}
                                                <span className='text-[14px] font-medium text-[#535862] dark:text-white font-inter mb-0 whitespace-nowrap'>
                                                    {carriage.commercialName.length === 1 ? `Carriage ${carriage.commercialName}` : carriage.commercialName}
                                                </span>
                                                <div className='w-full h-[1px] bg-[#E9EAEB] dark:bg-[#252B37] mb-2 mt-3'></div>
                                              
                                              <div className='relative'>
                                              <div className='flex flex-row items-center mt-[5px] absolute left-[-35px] bottom-[48%]'>
                                                <img src='/svg/keyboard_arrow_down.svg' className='w-2 h-2 object-contain rotate-90' alt='download' />
                                                           <div className='flex flex-row gap-[2px] items-center ml-[-5px] pb-[0.5px]'>
                                                                <div className='h-[1px] w-[1px] bg-black dark:bg-white'></div>
                                                                <div className='h-[1px] w-[1px] bg-black dark:bg-white'></div>
                                                                <div className='h-[1px] w-[1px] bg-black dark:bg-white'></div>
                                                                <div className='h-[1px] w-[1px] bg-black dark:bg-white'></div>
                                                                <div className='h-[1px] w-[1px] bg-black dark:bg-white'></div>
                                                                <div className='h-[1px] w-[1px] bg-black dark:bg-white'></div>
                                                                <div className='h-[1px] w-[1px] bg-black dark:bg-white'></div>
                                                           </div>
                                                           <div className='!h-[7px] !w-[7px] bg-black rounded-[20px] ml-[1px]  '/>
                                                </div>
                                                {hasTechnical ? (
                                                    /* Draggable card */
                                                    <div
                                                        draggable
                                                        onDragStart={(e) => onSlotDragStart(e, slotIdx)}
                                                        onDragOver={(e) => onSlotDragOver(e, slotIdx)}
                                                        onDrop={(e) => onSlotDrop(e, slotIdx)}
                                                        onDragEnd={onSlotDragEnd}
                                                        className={`w-[100px] rounded-[4px] relative border justify-between align-left bg-white dark:bg-[#252B37] p-3 h-[100px] w-[140px] flex flex-col items-left gap-1.5 shadow-sm transition-all cursor-grab active:cursor-grabbing ${isBeingDragged ? 'opacity-50 scale-95 border-gray-300' : ''
                                                            } ${isDropTarget ? 'border-[#E9EAEB] border-2 shadow-md bg-blue-50' : 'border-gray-200 dark:border-[#252B37] hover:shadow-md'}`}
                                                    >
                                                        
                                                        {/* <SubwayIcon size={28} color='#252B37' /> */}
                                                        <span className='text-[16px] font-medium text-[#252B37] dark:text-white font-inter'>{carriage.technicalName}</span>
                                                        <div className='flex flex-row items-center'>
                                                            
                                                        <div className='flex items-center gap-1'>
                                                            {/* <CabinIcon size={14} /> */}
                                                            <img src='/svg/night_shelter.svg' className='w-5 h-5 object-contain dark:contrast-0' alt='download' />
                                                            
                                                           
                                                           {/* <div className='!h-[5px] !w-[5px] bg-black rounded-full pl-[1px]'/> */}
                                                                </div>
                                                                <span className='text-[14px] text-[#252B37] dark:text-white font-inter font-normal'>{carriage.numberOfCabins} {carriage.numberOfCabins === 1 ? 'Cabin' : 'Cabins'}</span>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    /* Empty placeholder slot */
                                                    <div
                                                        onDragOver={(e) => onSlotDragOver(e, slotIdx)}
                                                        onDrop={(e) => onSlotDrop(e, slotIdx)}
                                                        className={`w-[140px] h-[100px] rounded-[4px] border-2 border-dashed flex items-center justify-center transition-all ${isDropTarget ? 'border-[#2D8EFF] bg-blue-50' : 'border-gray-200 dark:border-[#252B37] bg-gray-50 dark:bg-[#252B37]'
                                                            }`}
                                                    >
                                                        <span className='text-[11px] text-[#A4A7AE] text-center px-1'>No carriage assigned</span>
                                                    </div>
                                                )}
                                                </div>
                                            </div>
                                        </React.Fragment>
                                    );
                                })}
                            </div>
                        </div>
                    </div>

                </div>

                {/* ---------- Footer ---------- */}
                <div className=' border-gray-200 px-0 py-0 flex flex-col gap-2 shrink-0'>
                    {/* Error */}
                    {submitErr && (
                        <div className='bg-red-50 border border-red-200 dark:border-[#252B37] dark:bg-[#252B37] rounded-lg px-4 py-2.5 mt-3'>
                            <p className='text-sm text-red-600'>{submitErr}</p>
                        </div>
                    )}
                    {/* Create Trip button */}
                    <button
                        onClick={handleSubmit}
                        disabled={loadingSubmit}
                        className='bg-[#E9EAEB] dark:bg-[#252B37] text-[#101010] dark:text-white font-inter text-[16px]  h-[51px] w-[200px] px-8 py-2.5 rounded-[6px]  font-medium hover:opacity-90 disabled:opacity-50 transition-opacity ml-auto'
                    >
                        {loadingSubmit ? 'Saving...' : data ? 'Save Changes' : 'Create Trip'}
                    </button>
                </div>
            </div>
        </Modal>
    );
};

export default ManageTripsModal;
